import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import { useWorkspace } from "../../contexts/Workspacecontext";
import "./JobDrives.scss";
import ShareMenu from "./ShareMenu";
import QRShareModal from "../../components/QrShareModal/QRShareModal";
// Add after existing imports
import LockedButton from "../../components/LockedButton";
import { RECRUITMENT, CONSULTANCY } from "../../config/moduleAccess";

// ─── API config ───────────────────────────────────────────────────────────────

const COMPANY_API = `${process.env.REACT_APP_API_HR_URL}/company/job-drives`;
const COMPANY_POSTS = `${process.env.REACT_APP_API_HR_URL}/recruiter/recruitment/jobposts/open`;
const CONSULTANT_BASE =
  process.env.REACT_APP_API_CONSULTANT_URL || process.env.REACT_APP_API_HR_URL;
const CONSULTANT_API = `${CONSULTANT_BASE}/consultancy/job-drive-or-walkin`;
const CONSULTANT_POSTS = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/jobpost/open`;
const CONSULTANT_DRIVE_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/job-drive-or-walkin`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB");
};

const fmtTime = (time) => {
  if (!time) return "—";
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(time)) {
    return time.slice(0, 5);
  }
  const d = new Date(time);
  return isNaN(d)
    ? "—"
    : d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
};

const timeObjToStr = (t) => {
  if (!t || t.hour == null) return "";
  return `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
};

const isoToDate = (iso) => {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return iso.split("T")[0];
};

const isoToTime = (iso) => {
  if (!iso) return "";
  if (/^\d{2}:\d{2}/.test(iso)) return iso.slice(0, 5);
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const modeIcon = (m) => (m === "ONLINE" ? "🌐" : m === "OFFLINE" ? "📍" : "🔀");

const normalizeTime = (t) => {
  if (!t) return "";
  if (typeof t === "string") return t;
  if (typeof t === "object") return timeObjToStr(t);
  return "";
};

const normaliseDrive = (d, isConsultant) => {
  if (!isConsultant) return d;
  return {
    ...d,
    jobDriveId: d.consultancyJobDriveId,
    jobDriveOrWalkinUrl: d.jobDriveOrWalkinurl,
    name: d.name || "", // ← add this
    fromTime: normalizeTime(d.fromTime),
    toTime: normalizeTime(d.toTime),
    postToLinkedIn: d.postOnLinkedIn ?? false,
    postToNaukri: d.postOnNaukriIn ?? false,
    jobPosts: (d.consultancyJobPosts || []).map((p) => ({
      ...p,
      jobPostId: p.consultancyJobPostId,
    })),
  };
};

const getDriveStatus = (d) => {
  const today = new Date().toISOString().split("T")[0];
  const from = d.fromDate ? isoToDate(d.fromDate) : null;
  const to = d.toDate ? isoToDate(d.toDate) : null;
  if (!from || !to) return { label: "Unknown", cls: "jd-badge" };
  if (to < today) return { label: "Completed", cls: "jd-badge jd-badge--done" };
  if (from > today)
    return { label: "Upcoming", cls: "jd-badge jd-badge--upcoming" };
  return { label: "Ongoing", cls: "jd-badge jd-badge--ongoing" };
};

const isUpcomingDrive = (drive) => {
  const today = new Date().toISOString().split("T")[0];
  const from = drive.fromDate ? isoToDate(drive.fromDate) : null;
  return !!from && from > today;
};

const isOngoingDrive = (drive) => {
  const today = new Date().toISOString().split("T")[0];
  const from = drive.fromDate ? isoToDate(drive.fromDate) : null;
  const to = drive.toDate ? isoToDate(drive.toDate) : null;
  return !!from && !!to && from <= today && to >= today;
};

const isCompletedDrive = (drive) => {
  const today = new Date().toISOString().split("T")[0];
  const to = drive.toDate ? isoToDate(drive.toDate) : null;
  return !!to && to < today;
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const F = ({ label, required, error, hint, children }) => (
  <div className={`jd-field${error ? " jd-field--error" : ""}`}>
    <label className="jd-field__label">
      {label}
      {required && <span className="jd-field__req"> *</span>}
    </label>
    {children}
    {hint && !error && <span className="jd-field__hint">{hint}</span>}
    {error && <span className="jd-field__error">{error}</span>}
  </div>
);

const Toggle = ({ label, name, checked, onChange, icon, disabled }) => (
  <label className={`jd-toggle${disabled ? " jd-toggle--disabled" : ""}`}>
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <span className="jd-toggle__track">
      <span className="jd-toggle__thumb" />
    </span>
    <span className="jd-toggle__label">
      {icon} {label}
      {disabled && (
        <span className="jd-toggle__posted-badge"> · Already posted</span>
      )}
    </span>
  </label>
);

const emptyForm = {
  name: "", // ← add this
  mode: "OFFLINE",
  fromDateTime: "",
  toDateTime: "",
  venue: "",
  shareToCandidates: true,
  postToLinkedIn: false,
  postToNaukri: false,
  jobPostIds: [],
  showBudget: true, // ← add this
  applicationClosingDate: "", // ← add this
};

const AppliedCandidatesModal = ({ drive, candidates, loading, onClose }) => {
  if (!drive) return null;
  const initials = (name) =>
    name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <div className="jd-modal-overlay" onClick={onClose}>
      <div
        className="jd-modal jd-modal--applied"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jd-modal__header">
          <div>
            <h3 className="jd-modal__title">Applied Candidates</h3>
            <p className="jd-modal__sub">Drive #{drive.jobDriveId}</p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="jd-modal__body">
          {loading ? (
            <div className="jd-loader">Loading candidates...</div>
          ) : candidates.length === 0 ? (
            <div className="jd-empty">No candidates have applied yet.</div>
          ) : (
            <div className="jd-applied-list">
              {candidates.map((c) => (
                <div key={c.appliedHistoryId} className="jd-applied-item">
                  <div className="jd-applied-item__avatar">
                    {initials(c.candidateName)}
                  </div>
                  <div className="jd-applied-item__info">
                    <div className="jd-applied-item__name">
                      {c.candidateName}
                    </div>
                    <div className="jd-applied-item__meta">
                      <span>{c.email}</span>
                      {c.contactNumber && <span>{c.contactNumber}</span>}
                    </div>
                  </div>
                  <span className="jd-applied-item__id">#{c.candidateId}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="jd-modal__actions">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DeleteResponseModal ──────────────────────────────────────────────────────
const DeleteResponseModal = ({
  response,
  onForceDelete,
  onClose,
  forceLoading,
}) => {
  if (!response) return null;

  const isConflict = response.isConflict;
  const message = response.message || "Operation completed.";
  const details = response.details || null;

  return (
    <div className="jd-modal-overlay" onClick={onClose}>
      <div
        className={`jd-modal jd-modal--response ${isConflict ? "jd-modal--conflict" : "jd-modal--success"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jd-modal__icon">{isConflict ? "⚠️" : "✅"}</div>
        <h3 className="jd-modal__title">
          {isConflict ? "Cannot Delete" : "Deleted Successfully"}
        </h3>
        <p className="jd-modal__message">{message}</p>

        {details && (
          <div className="jd-modal__details">
            <pre>
              {typeof details === "object"
                ? JSON.stringify(details, null, 2)
                : details}
            </pre>
          </div>
        )}

        {isConflict && (
          <div className="jd-modal__conflict-info">
            <p>
              Force delete will <strong>remove all mappings</strong> and
              candidate history associated with this drive and permanently
              delete it.
            </p>
          </div>
        )}

        <div className="jd-modal__actions">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            {isConflict ? "Cancel" : "Close"}
          </button>
          {isConflict && (
            <button
              className="cv-btn cv-btn--danger"
              onClick={onForceDelete}
              disabled={forceLoading}
            >
              {forceLoading ? (
                <span className="cv-btn__spinner" />
              ) : (
                <>🗑️ Force Delete</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ConsultantDriveAppliedCandidates — sidebar drives + candidates panel
// ─────────────────────────────────────────────────────────────────────────────
const ConsultantDriveAppliedCandidates = ({
  drives,
  initialDrive,
  onClose,
}) => {
  const [selectedDrive, setSelectedDrive] = useState(
    initialDrive || drives[0] || null,
  );
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedDrive) return;
    setLoading(true);
    setCandidates([]);
    axios
      .get(
        `${CONSULTANT_DRIVE_API}/${selectedDrive.jobDriveId}/applied-candidates`,
      )
      .then((r) => setCandidates(r.data.data || []))
      .catch(() => toast.error("Failed to load candidates."))
      .finally(() => setLoading(false));
  }, [selectedDrive]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        c.candidateName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.jobTitle?.toLowerCase().includes(q),
    );
  }, [candidates, search]);

  const getInitials = (name) =>
    String(name || "")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?";

  const avatarColor = (name) => {
    const colors = [
      "#6366f1",
      "#0ea5e9",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
    ];
    return colors[(String(name || "").charCodeAt(0) || 0) % colors.length];
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide" style={{ width: "780px" }}>
        {/* Header */}
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Applied Candidates</h2>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body — sidebar + main */}
        <div
          className="drawer__body"
          style={{
            padding: 0,
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* ── Left sidebar: drive list ── */}
          <div
            style={{
              width: 220,
              flexShrink: 0,
              borderRight: "1px solid #f3f4f6",
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            <div
              style={{
                padding: "12px 14px 8px",
                fontSize: 10,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: "0.06em",
                borderBottom: "1px solid #f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Job Drive</span>
              <span
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 8,
                }}
              >
                {drives.length}
              </span>
            </div>

            {drives.map((d) => {
              const status = getDriveStatus(d);
              const isActive = selectedDrive?.jobDriveId === d.jobDriveId;
              return (
                <button
                  key={d.jobDriveId}
                  type="button"
                  onClick={() => {
                    setSelectedDrive(d);
                    setSearch("");
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "5px 10px",
                    border: "none",
                    borderBottom: "1px solid #f3f4f6",
                    background: isActive ? "#eff6ff" : "transparent",
                    borderLeft: isActive
                      ? "3px solid #2563eb"
                      : "3px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.12s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color:
                          status.label === "Upcoming"
                            ? "#15803d"
                            : status.label === "Ongoing"
                              ? "#b45309"
                              : "#6b7280",
                      }}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isActive ? "#1e40af" : "#111827",
                      marginBottom: 2,
                    }}
                  >
                    {d.name || `Drive #${d.jobDriveId}`}
                  </div>
                  <div
                    style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}
                  >
                    {fmtDate(d.fromDate)}
                    {d.fromDate !== d.toDate && ` → ${fmtDate(d.toDate)}`}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      className={`jd-mode-badge jd-mode-badge--${d.mode?.toLowerCase()}`}
                      style={{ fontSize: 10, padding: "1px 6px" }}
                    >
                      {d.mode}
                    </span>
                    {d.jobPosts?.length > 0 && (
                      <span style={{ color: "#9ca3af" }}>
                        · {d.jobPosts.length} post
                        {d.jobPosts.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Right: candidates ── */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Drive summary bar */}
            {selectedDrive && (
              <div
                style={{
                  padding: "10px 16px",
                  borderBottom: "1px solid #f3f4f6",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  className={`jd-mode-badge jd-mode-badge--${selectedDrive.mode?.toLowerCase()}`}
                >
                  {modeIcon(selectedDrive.mode)} {selectedDrive.mode}
                </span>
                {selectedDrive.venue && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    📍 {selectedDrive.venue}
                  </span>
                )}
                {selectedDrive.jobPosts?.length > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {selectedDrive.jobPosts.slice(0, 3).map((p) => (
                      <span
                        key={p.jobPostId}
                        style={{
                          fontSize: 10,
                          background: "rgba(37,99,235,0.08)",
                          color: "#2563eb",
                          border: "1px solid rgba(37,99,235,0.14)",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontWeight: 500,
                        }}
                      >
                        {p.jobTitle}
                      </span>
                    ))}
                    {selectedDrive.jobPosts.length > 3 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          padding: "2px 6px",
                        }}
                      >
                        +{selectedDrive.jobPosts.length - 3} more
                      </span>
                    )}
                  </div>
                )}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {loading
                    ? "Loading…"
                    : `${filtered.length} candidate${filtered.length !== 1 ? "s" : ""}`}
                </span>
              </div>
            )}

            {/* Search */}
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <div className="cand-search-wrap">
                <svg
                  className="cand-search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <circle
                    cx="6.5"
                    cy="6.5"
                    r="4.5"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10.5 10.5l3 3"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  className="cand-search"
                  placeholder="Search candidates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="cand-search-clear"
                    onClick={() => setSearch("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Candidates list */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {loading ? (
                <div className="jd-loader">Loading candidates...</div>
              ) : filtered.length === 0 ? (
                <div className="jd-empty">
                  <span className="jd-empty__icon">👥</span>
                  <p>
                    {search
                      ? "No candidates match your search."
                      : "No candidates have applied yet."}
                  </p>
                </div>
              ) : (
                <table className="cand-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>SL No</th>
                      <th>Candidate</th>
                      <th>Contact</th>
                      <th>Applied For</th>
                      <th>Salary</th>
                      <th>Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <tr key={c.appliedHistoryId}>
                        <td className="cand-td-num">{i + 1}</td>
                        <td>
                          <div className="cand-identity">
                            <div
                              className="cand-avatar"
                              style={{
                                background: avatarColor(c.candidateName),
                              }}
                            >
                              {getInitials(c.candidateName)}
                            </div>
                            <div>
                              <div className="cand-name">
                                {c.candidateName || "—"}
                              </div>
                              <div className="cand-email">{c.email || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="cand-contact">
                          {c.contactNumber || "—"}
                        </td>
                        <td>
                          {c.jobTitle ? (
                            <span
                              style={{
                                fontSize: 12,
                                background: "rgba(37,99,235,0.08)",
                                color: "#2563eb",
                                border: "1px solid rgba(37,99,235,0.14)",
                                padding: "3px 10px",
                                borderRadius: 999,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.jobTitle}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ fontSize: 13, color: "#374151" }}>
                          {c.expectedSalary
                            ? `₹${Number(c.expectedSalary).toLocaleString("en-IN")}`
                            : "—"}
                        </td>
                        <td>
                          {c.scheduledInterview ? (
                            <span className="cand-scheduled-badge">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <circle
                                  cx="6"
                                  cy="6"
                                  r="5"
                                  stroke="#16a34a"
                                  strokeWidth="1.3"
                                />
                                <path
                                  d="M3.5 6l1.8 1.8L8.5 4"
                                  stroke="#16a34a"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Scheduled
                            </span>
                          ) : (
                            <span className="cand-na">Not yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CompanyDriveAppliedCandidates — sidebar drives + candidates panel
// ─────────────────────────────────────────────────────────────────────────────
const CompanyDriveAppliedCandidates = ({ drives, onClose }) => {
  const [selectedDrive, setSelectedDrive] = useState(drives[0] || null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedDrive) return;
    setLoading(true);
    setCandidates([]);
    axios
      .get(`${COMPANY_API}/${selectedDrive.jobDriveId}/applied-candidates`)
      .then((r) => setCandidates(r.data.data || []))
      .catch(() => toast.error("Failed to load candidates."))
      .finally(() => setLoading(false));
  }, [selectedDrive]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        c.candidateName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        String(c.candidateId).includes(q),
    );
  }, [candidates, search]);

  const getInitials = (name) =>
    String(name || "")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?";

  const avatarColor = (name) => {
    const colors = [
      "#6366f1",
      "#0ea5e9",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
    ];
    return colors[(String(name || "").charCodeAt(0) || 0) % colors.length];
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide" style={{ width: "780px" }}>
        {/* Header */}
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Applied Candidates</h2>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body — sidebar + main */}
        <div
          className="drawer__body"
          style={{
            padding: 0,
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* ── Left sidebar: drive list ── */}
          <div
            style={{
              width: 220,
              flexShrink: 0,
              borderRight: "1px solid #f3f4f6",
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            <div
              style={{
                padding: "12px 14px 8px",
                fontSize: 10,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: "0.06em",
                borderBottom: "1px solid #f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Job Drive</span>
              <span
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 8,
                }}
              >
                {drives.length}
              </span>
            </div>

            {drives.map((d) => {
              const status = getDriveStatus(d);
              const isActive = selectedDrive?.jobDriveId === d.jobDriveId;
              return (
                <button
                  key={d.jobDriveId}
                  type="button"
                  onClick={() => {
                    setSelectedDrive(d);
                    setSearch("");
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "5px 10px",
                    border: "none",
                    borderBottom: "1px solid #f3f4f6",
                    background: isActive ? "#eff6ff" : "transparent",
                    borderLeft: isActive
                      ? "3px solid #2563eb"
                      : "3px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.12s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color:
                          status.label === "Upcoming"
                            ? "#15803d"
                            : status.label === "Ongoing"
                              ? "#b45309"
                              : "#6b7280",
                      }}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isActive ? "#1e40af" : "#111827",
                      marginBottom: 2,
                    }}
                  >
                    {d.name || `Drive #${d.jobDriveId}`}
                  </div>
                  <div
                    style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}
                  >
                    {fmtDate(d.fromDate)}
                    {d.fromDate !== d.toDate && ` → ${fmtDate(d.toDate)}`}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      className={`jd-mode-badge jd-mode-badge--${d.mode?.toLowerCase()}`}
                      style={{ fontSize: 10, padding: "1px 6px" }}
                    >
                      {d.mode}
                    </span>
                    {d.jobPosts?.length > 0 && (
                      <span style={{ color: "#9ca3af" }}>
                        · {d.jobPosts.length} post
                        {d.jobPosts.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Right: candidates ── */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Drive summary bar */}
            {selectedDrive && (
              <div
                style={{
                  padding: "10px 16px",
                  borderBottom: "1px solid #f3f4f6",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  className={`jd-mode-badge jd-mode-badge--${selectedDrive.mode?.toLowerCase()}`}
                >
                  {modeIcon(selectedDrive.mode)} {selectedDrive.mode}
                </span>
                {selectedDrive.venue && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    📍 {selectedDrive.venue}
                  </span>
                )}
                {selectedDrive.jobPosts?.length > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {selectedDrive.jobPosts.slice(0, 3).map((p) => (
                      <span
                        key={p.jobPostId}
                        style={{
                          fontSize: 10,
                          background: "rgba(37,99,235,0.08)",
                          color: "#2563eb",
                          border: "1px solid rgba(37,99,235,0.14)",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontWeight: 500,
                        }}
                      >
                        {p.jobTitle}
                      </span>
                    ))}
                    {selectedDrive.jobPosts.length > 3 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          padding: "2px 6px",
                        }}
                      >
                        +{selectedDrive.jobPosts.length - 3} more
                      </span>
                    )}
                  </div>
                )}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {loading
                    ? "Loading…"
                    : `${filtered.length} candidate${filtered.length !== 1 ? "s" : ""}`}
                </span>
              </div>
            )}

            {/* Search */}
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <div className="cand-search-wrap">
                <svg
                  className="cand-search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <circle
                    cx="6.5"
                    cy="6.5"
                    r="4.5"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10.5 10.5l3 3"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  className="cand-search"
                  placeholder="Search candidates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="cand-search-clear"
                    onClick={() => setSearch("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Candidates list */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {loading ? (
                <div className="jd-loader">Loading candidates...</div>
              ) : filtered.length === 0 ? (
                <div className="jd-empty">
                  <span className="jd-empty__icon">👥</span>
                  <p>
                    {search
                      ? "No candidates match your search."
                      : "No candidates have applied yet."}
                  </p>
                </div>
              ) : (
                <table className="cand-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Candidate</th>
                      <th>Contact</th>
                      <th>Candidate ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <tr key={c.appliedHistoryId}>
                        <td className="cand-td-num">{i + 1}</td>
                        <td>
                          <div className="cand-identity">
                            <div
                              className="cand-avatar"
                              style={{
                                background: avatarColor(c.candidateName),
                              }}
                            >
                              {getInitials(c.candidateName)}
                            </div>
                            <div>
                              <div className="cand-name">
                                {c.candidateName || "—"}
                              </div>
                              <div className="cand-email">{c.email || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="cand-contact">
                          {c.contactNumber || "—"}
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: 12,
                              fontFamily: "monospace",
                              background: "#f3f4f6",
                              color: "#374151",
                              padding: "2px 8px",
                              borderRadius: 6,
                            }}
                          >
                            #{c.candidateId}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

const exportDrivesCSV = (data) => {
  const headers = [
    "Drive ID",
    "Drive Name",
    "Mode",
    "From Date",
    "From Time",
    "To Date",
    "To Time",
    "Venue",
    "Status",
    "Share To Candidates",
    // "Post To LinkedIn",
    // "Post To Naukri",
    "Show Budget",
    "Application Closing Date",
    "Job Posts Count",
    "Job Posts",
  ];

  const escapeCSV = (val) => {
    const str = val == null ? "" : String(val);

    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const rows = data.map((d) => {
    const status = getDriveStatus(d).label;

    return [
      d.jobDriveId || "",
      d.name || "",
      d.mode || "",
      fmtDate(d.fromDate),
      fmtTime(d.fromTime),
      fmtDate(d.toDate),
      fmtTime(d.toTime),
      d.venue || "",
      status,
      d.shareToCandidates ? "Yes" : "No",
      d.postToLinkedIn ? "Yes" : "No",
      d.postToNaukri ? "Yes" : "No",
      d.showBudget ? "Yes" : "No",
      fmtDate(d.applicationClosingDate),
      d.jobPosts?.length || 0,
      d.jobPosts?.map((p) => p.jobTitle).join(" | ") || "",
    ]
      .map(escapeCSV)
      .join(",");
  });

  const csv = [headers.join(","), ...rows].join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `job-drives-${new Date().toISOString().slice(0, 10)}.csv`;

  link.click();

  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────
//  AllJobDrives
// ─────────────────────────────────────────────────────────────────────────────
const AllJobDrives = () => {
  const { isConsultant } = useWorkspace();

  const createDriveCode = isConsultant
    ? CONSULTANCY.JOB_DRIVES_CREATE
    : RECRUITMENT.JOB_DRIVES_CREATE;

  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  const [appliedTarget, setAppliedTarget] = useState(null);
  const [appliedCandidates, setAppliedCandidates] = useState([]);
  const [appliedLoading, setAppliedLoading] = useState(false);

  // delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResponse, setDeleteResponse] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ mode: "", dateRange: "" });
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const [appliedDrawerOpen, setAppliedDrawerOpen] = useState(false);
  const [companyAppliedOpen, setCompanyAppliedOpen] = useState(false);

  const [selectedAppliedDrive, setSelectedAppliedDrive] = useState(null);

  const fetchDrives = useCallback(() => {
    setLoading(true);
    const url = isConsultant ? `${CONSULTANT_API}/getAll` : COMPANY_API;
    axios
      .get(url)
      .then((res) => {
        const raw = res.data.data || [];
        setDrives(raw.map((d) => normaliseDrive(d, isConsultant)));
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [isConsultant]);

  useEffect(() => {
    fetchDrives();
  }, [fetchDrives]);

  // ── Normal delete ────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const url = isConsultant
        ? `${CONSULTANT_API}/delete/${deleteTarget.jobDriveId}`
        : `${COMPANY_API}/${deleteTarget.jobDriveId}`;

      const res = await axios.delete(url);
      const msg = res.data?.message || "Job drive deleted successfully.";

      setDeleteTarget(null);
      fetchDrives();
      toast.success(msg);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to delete job drive.";
      const details = err?.response?.data?.details || null;

      if (status === 409 || status === 400) {
        setDeleteResponse({ isConflict: true, message: msg, details });
      } else {
        setDeleteTarget(null);
        toast.error(msg);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleForceDelete = async () => {
    if (!deleteTarget) return;
    setForceLoading(true);
    try {
      const url = isConsultant
        ? `${CONSULTANT_API}/delete/${deleteTarget.jobDriveId}/force`
        : `${COMPANY_API}/${deleteTarget.jobDriveId}/force`;

      const res = await axios.delete(url);
      const msg = res.data?.message || "Job drive force deleted successfully.";

      setDeleteResponse(null);
      setDeleteTarget(null);
      fetchDrives();
      toast.success(msg);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Force delete failed.";
      const details = err?.response?.data?.details || null;
      setDeleteResponse({ isConflict: false, message: msg, details });
      toast.error(msg);
    } finally {
      setForceLoading(false);
    }
  };

  const handleViewApplied = async (drive) => {
    setAppliedTarget(drive);
    setAppliedLoading(true);
    setAppliedCandidates([]);
    try {
      const url = `${process.env.REACT_APP_API_HR_URL}/company/job-drives/${drive.jobDriveId}/applied-candidates`;
      const res = await axios.get(url);
      setAppliedCandidates(res.data.data || []);
    } catch (err) {
      toast.error("Failed to load applied candidates.");
    } finally {
      setAppliedLoading(false);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setDrawerOpen(true);
  };
  const openEdit = (d) => {
    setEditTarget(d);
    setDrawerOpen(true);
  };

  const driveStats = [
    {
      label: "Total Events",
      value: drives.length,
      tone: "blue",
      sub: `${drives.reduce((sum, drive) => sum + (drive.jobPosts?.length || 0), 0)} linked job posts`,
    },
    {
      label: "Upcoming",
      value: drives.filter(isUpcomingDrive).length,
      tone: "green",
      sub: "Scheduled ahead of today",
    },
    {
      label: "Ongoing",
      value: drives.filter(isOngoingDrive).length,
      tone: "amber",
      sub: "Currently active drives",
    },
    {
      label: "Completed",
      value: drives.filter(isCompletedDrive).length,
      tone: "violet",
      sub: "Past events in history",
    },
  ];

  const filterCounts = useMemo(
    () => ({
      total: drives.length,
      UPCOMING: drives.filter(isUpcomingDrive).length,
      ONGOING: drives.filter(isOngoingDrive).length,
      COMPLETED: drives.filter(isCompletedDrive).length,
    }),
    [drives],
  );

  const filteredDrives = useMemo(() => {
    return drives.filter((d) => {
      const status = getDriveStatus(d).label.toUpperCase();

      const matchStatus = statusFilter === "ALL" || status === statusFilter;

      const matchMode =
        !filters.mode || (d.mode || "").toUpperCase() === filters.mode;

      const matchSearch = (() => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
          d.name?.toLowerCase().includes(q) || // ← add this
          d.venue?.toLowerCase().includes(q) ||
          d.jobPosts?.some((p) => p.jobTitle?.toLowerCase().includes(q)) ||
          String(d.jobDriveId).includes(q)
        );
      })();

      const matchDate = (() => {
        if (!filters.dateRange) return true;
        const today = new Date().toISOString().split("T")[0];
        const from = d.fromDate ? isoToDate(d.fromDate) : null;
        if (filters.dateRange === "future") return !!from && from >= today;
        if (filters.dateRange === "past") return !!from && from < today;
        return true;
      })();

      return matchStatus && matchMode && matchSearch && matchDate;
    });
  }, [drives, statusFilter, filters, search]);

  return (
    <div className="jd-page">
      <div className="jd-page__header">
        <div className="jd-page__header-main">
          <div className="jd-page__title-wrap">
            <h2 className="jd-page__title">Job Drives & Walk-ins</h2>
            <span className="jd-page__count">
              {filteredDrives.length} total
            </span>
          </div>
          <p className="jd-page__sub">
            Plan events, publish openings, and track every drive in the same
            polished workspace style.
          </p>
        </div>
        {isConsultant && drives.length > 0 && (
          <button
            className="jd-create-btn"
            style={{
              background: "#f0fdf4",
              color: "#15803d",
              border: "1.5px solid #bbf7d0",
              boxShadow: "none",
            }}
            onClick={() => setAppliedDrawerOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle
                cx="5"
                cy="4.5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M1 12c0-2.21 1.79-4 4-4s4 1.79 4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle
                cx="11.5"
                cy="4.5"
                r="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M11.5 8.5c1.93 0 3.5 1.57 3.5 3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Applied Candidates
          </button>
        )}
        {!isConsultant && drives.length > 0 && (
          <button
            className="jd-create-btn"
            style={{
              background: "#f0fdf4",
              color: "#15803d",
              border: "1.5px solid #bbf7d0",
              boxShadow: "none",
            }}
            onClick={() => setCompanyAppliedOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle
                cx="5"
                cy="4.5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M1 12c0-2.21 1.79-4 4-4s4 1.79 4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle
                cx="11.5"
                cy="4.5"
                r="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M11.5 8.5c1.93 0 3.5 1.57 3.5 3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Applied Candidates
          </button>
        )}
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <button
            className="jd-top-action-btn"
            onClick={() => exportDrivesCSV(filteredDrives)}
            disabled={filteredDrives.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>

          <LockedButton
            codes={[createDriveCode]}
            className="jd-create-btn jd-create-btn--primary"
            onClick={openCreate}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 1v12M1 7h12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Create Drive
          </LockedButton>
        </div>
      </div>

      {!loading && (
        <div className="jd-stats-row">
          {driveStats.map((stat) => (
            <article
              key={stat.label}
              className={`jd-stat jd-stat--${stat.tone}`}
            >
              <span className="jd-stat__bar" />
              <div className="jd-stat__body">
                <div className="jd-stat__text">
                  <p className="jd-stat__label">{stat.label}</p>
                  <p className="jd-stat__sub">{stat.sub}</p>
                </div>
                <h3 className="jd-stat__value">{stat.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && drives.length > 0 && (
        <>
          {/* ── All filter pills in one line ───────────────────────────────── */}
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 10,
              alignItems: "center",
            }}
          >
            {/* Status */}
            {[
              { value: "ALL", label: "All", dot: null },
              { value: "UPCOMING", label: "Upcoming", dot: "#22c55e" },
              { value: "ONGOING", label: "Ongoing", dot: "#f59e0b" },
              { value: "COMPLETED", label: "Completed", dot: "#6b7280" },
            ].map((opt) => {
              const count =
                opt.value === "ALL"
                  ? filterCounts.total
                  : (filterCounts[opt.value] ?? 0);
              const isActive = statusFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 10px",
                    borderRadius: 20,
                    border: isActive
                      ? "1.5px solid #2563eb"
                      : "1.5px solid #e5e7eb",
                    background: isActive ? "#eff6ff" : "#fff",
                    color: isActive ? "#1d4ed8" : "#374151",
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                  }}
                >
                  {opt.dot && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: opt.dot,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {opt.label}
                  <span
                    style={{
                      background: isActive ? "#dbeafe" : "#f3f4f6",
                      color: isActive ? "#1d4ed8" : "#6b7280",
                      borderRadius: 10,
                      padding: "1px 6px",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <span
              style={{
                width: 2,
                height: 32,
                background: "#94a3b8",
                borderRadius: 999,
                flexShrink: 0,
                opacity: 0.8,
              }}
            />

            {/* Mode */}
            {[
              { value: "", label: "All Modes", dot: null },
              { value: "ONLINE", label: "Online", dot: "#22c55e" },
              { value: "OFFLINE", label: "Offline", dot: "#f59e0b" },
              { value: "HYBRID", label: "Hybrid", dot: "#7c3aed" },
            ].map((opt) => {
              const isActive = filters.mode === opt.value;
              return (
                <button
                  key={`mode-${opt.value}`}
                  type="button"
                  onClick={() => setFilters((p) => ({ ...p, mode: opt.value }))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 10px",
                    borderRadius: 20,
                    border: isActive
                      ? "1.5px solid #2563eb"
                      : "1.5px solid #e5e7eb",
                    background: isActive ? "#eff6ff" : "#fff",
                    color: isActive ? "#1d4ed8" : "#374151",
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                  }}
                >
                  {opt.dot && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: opt.dot,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {opt.label}
                </button>
              );
            })}

            {/* Divider */}
            <span
              style={{
                width: 2,
                height: 32,
                background: "#94a3b8",
                borderRadius: 999,
                flexShrink: 0,
                opacity: 0.8,
              }}
            />

            {/* Date Range */}
            {[
              { value: "", label: "All Dates", dot: null },
              { value: "future", label: "Upcoming only", dot: "#2563eb" },
              { value: "past", label: "Past only", dot: "#6b7280" },
            ].map((opt) => {
              const isActive = filters.dateRange === opt.value;
              return (
                <button
                  key={`date-${opt.value}`}
                  type="button"
                  onClick={() =>
                    setFilters((p) => ({ ...p, dateRange: opt.value }))
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 10px",
                    borderRadius: 20,
                    border: isActive
                      ? "1.5px solid #2563eb"
                      : "1.5px solid #e5e7eb",
                    background: isActive ? "#eff6ff" : "#fff",
                    color: isActive ? "#1d4ed8" : "#374151",
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                  }}
                >
                  {opt.dot && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: opt.dot,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {opt.label}
                </button>
              );
            })}

            {/* Clear all — only when filters active */}
            {(hasActiveFilters || statusFilter !== "ALL") && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("ALL");
                  setFilters({ mode: "", dateRange: "" });
                  setSearch("");
                }}
                style={{
                  marginLeft: "auto",
                  padding: "5px 10px",
                  borderRadius: 20,
                  border: "1.5px solid #fecdd3",
                  background: "#fff1f2",
                  color: "#dc2626",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                ✕ Clear all
              </button>
            )}
          </div>

          {/* ── Search ─────────────────────────────────────────────────────────── */}
          <div className="cand-search-wrap" style={{ marginBottom: 10 }}>
            <svg
              className="cand-search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <circle
                cx="6.5"
                cy="6.5"
                r="4.5"
                stroke="#9ca3af"
                strokeWidth="1.5"
              />
              <path
                d="M10.5 10.5l3 3"
                stroke="#9ca3af"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="cand-search"
              placeholder="Search name, venue, job title, drive ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="cand-search-clear"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            )}
          </div>
        </>
      )}

      {/* ── Main content ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="jd-loader">Loading job drives...</div>
      ) : drives.length === 0 ? (
        <div className="jd-empty">
          <span className="jd-empty__icon">🗓️</span>
          <p>No job drives scheduled yet.</p>
          <LockedButton
            codes={[createDriveCode]}
            className="jd-create-btn"
            onClick={openCreate}
          >
            Create your first drive
          </LockedButton>
        </div>
      ) : filteredDrives.length === 0 ? (
        <div className="jd-empty">
          <span className="jd-empty__icon">📭</span>
          <p>No drives match your filters.</p>
          <button
            className="jd-create-btn"
            style={{ marginTop: 8 }}
            onClick={() => {
              setStatusFilter("ALL");
              setFilters({ mode: "", dateRange: "" });
              setSearch("");
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="jd-grid">
          {filteredDrives.map((d) => {
            const status = getDriveStatus(d);
            const statusClass = status.label.toLowerCase();
            // Budget shown only when this drive has showBudget enabled and
            // at least one linked job post carries a budget figure.
            const budgetPost = d.jobPosts?.find((p) => p.budget > 0);
            const budgetDisplay =
              d.showBudget && budgetPost
                ? `₹${Number(budgetPost.budget).toLocaleString("en-IN")}${
                    budgetPost.budgetType
                      ? ` / ${budgetPost.budgetType.toLowerCase()}`
                      : ""
                  }`
                : null;
            // Employee who created this drive — populated once the
            // "created by" wiring on the backend/response is in place.
            const createdByName =
              d.createdByName || d.createdBy?.name || d.createdByEmployeeName;

            return (
              <div
                key={d.jobDriveId}
                className={`jd-card jd-card--${statusClass}`}
              >
                <div className="jd-card__header">
                  <div className="jd-card__mode">
                    <span
                      className={`jd-mode-badge jd-mode-badge--${d.mode?.toLowerCase()}`}
                    >
                      {modeIcon(d.mode)} {d.mode}
                    </span>
                    <span className={status.cls}>{status.label}</span>
                  </div>
                  {d.name && ( // ← add this
                    <div className="jd-card__name">{d.name}</div>
                  )}
                </div>

                <div className="jd-card__datetime">
                  <div className="jd-card__date-block">
                    <span className="jd-card__date-label">From</span>
                    <span className="jd-card__date">{fmtDate(d.fromDate)}</span>
                    <span className="jd-card__time">{fmtTime(d.fromTime)}</span>
                  </div>
                  <div className="jd-card__arrow">→</div>
                  <div className="jd-card__date-block">
                    <span className="jd-card__date-label">To</span>
                    <span className="jd-card__date">{fmtDate(d.toDate)}</span>
                    <span className="jd-card__time">{fmtTime(d.toTime)}</span>
                  </div>
                </div>

                {d.venue && (
                  <div className="jd-card__venue">
                    <span>📍</span>
                    <span>{d.venue}</span>
                  </div>
                )}

                {(d.applicationClosingDate ||
                  createdByName ||
                  budgetDisplay) && (
                  <div className="jd-card__meta-row">
                    {d.applicationClosingDate && (
                      <span className="jd-card__meta-item">
                        🗓️ Apply by {fmtDate(d.applicationClosingDate)}
                      </span>
                    )}
                    {budgetDisplay && (
                      <span className="jd-card__meta-item">
                        💰 {budgetDisplay}
                      </span>
                    )}
                    {createdByName && (
                      <span className="jd-card__meta-item">
                        👤 {createdByName}
                      </span>
                    )}
                  </div>
                )}

                {d.jobPosts?.length > 0 && (
                  <div>
                    <div className="jd-card__posts-label">
                      Job Posts ({d.jobPosts.length})
                    </div>
                    <div className="jd-card__posts-list">
                      {d.jobPosts.slice(0, 2).map((p) => (
                        <span key={p.jobPostId} className="jd-card__post-tag">
                          {p.jobTitle}
                        </span>
                      ))}
                      {d.jobPosts.length > 2 && (
                        <span className="jd-card__post-tag jd-card__post-tag--more">
                          +{d.jobPosts.length - 2} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="jd-card__footer">
                  <button
                    className="jd-card__btn"
                    onClick={() => setDetailTarget(d)}
                  >
                    View Details
                  </button>

                  {!isConsultant && (
                    <button
                      className="jd-card__btn jd-card__btn--applied"
                      onClick={() => handleViewApplied(d)}
                    >
                      Applied
                    </button>
                  )}

                  {isConsultant && (
                    <button
                      className="jd-card__btn jd-card__btn--applied"
                      onClick={() => {
                        setSelectedAppliedDrive(d);
                        setAppliedDrawerOpen(true);
                      }}
                    >
                      Applied
                    </button>
                  )}

                  <div className="jd-card__icon-btns">
                    {d.jobDriveOrWalkinUrl && (
                      <ShareMenu
                        link={d.jobDriveOrWalkinUrl}
                        // AFTER
                        onShowQR={() => {
                          const driveStatus = getDriveStatus(d).label;
                          const totalVacancies =
                            d.jobPosts?.reduce(
                              (sum, p) => sum + (p.noOfVacancy || 0),
                              0,
                            ) || null;

                          // Collect unique roles/titles from linked posts
                          const postTitles =
                            d.jobPosts
                              ?.map((p) => p.jobTitle || p.role)
                              .filter(Boolean) || [];

                          // Use first post with a budget for salary display
                          const firstPostWithBudget = d.jobPosts?.find(
                            (p) => p.budget > 0,
                          );

                          // Build a description from linked posts
                          const jobDescription =
                            d.jobPosts?.length > 0
                              ? d.jobPosts
                                  .map((p, i) => {
                                    const title =
                                      p.jobTitle || p.role || "Open Role";
                                    const dept = p.department
                                      ? ` · ${p.department}`
                                      : "";
                                    const exp = p.experience
                                      ? ` · ${p.experience}`
                                      : "";
                                    const vac = p.noOfVacancy
                                      ? ` · ${p.noOfVacancy} opening${p.noOfVacancy !== 1 ? "s" : ""}`
                                      : "";
                                    return `${i + 1}. ${title}${dept}${exp}${vac}`;
                                  })
                                  .join("\n")
                              : null;

                          setQrTarget({
                            url: d.jobDriveOrWalkinUrl,
                            post: {
                              // Header
                              jobTitle: d.name || `Drive #${d.jobDriveId}`,
                              jobPostCode: `DRIVE-${d.jobDriveId}`,
                              status:
                                driveStatus === "Completed" ? "CLOSED" : "OPEN",

                              // Header badges
                              employmentType: d.mode, // shows as OFFLINE / ONLINE / HYBRID badge
                              branchCity: d.venue || null, // shows as 📍 location badge

                              // Details rows
                              noOfVacancy: totalVacancies,
                              experience:
                                d.jobPosts?.find((p) => p.experience)
                                  ?.experience || null,
                              budget: firstPostWithBudget?.budget || null,
                              budgetType:
                                firstPostWithBudget?.budgetType?.toUpperCase() ||
                                "ANNUAL",
                              salaryType:
                                firstPostWithBudget?.salaryType || null,

                              // Drive end date → "Apply by" row
                              expectedClosingTime: d.toDate || null,

                              // "About the role" text block — lists all linked posts
                              jobDescription,

                              // Skills tags — unique roles across all linked posts
                              skills: postTitles.join(", ") || null,

                              // Sharing flags → "Published on" section
                              shareToCandidates: d.shareToCandidates,
                              postOnLinkedIn: d.postToLinkedIn,
                              postOnNaukri: d.postToNaukri,
                            },
                          });
                        }}
                      />
                    )}
                    <button
                      className="jd-icon-btn jd-icon-btn--edit"
                      onClick={() => openEdit(d)}
                      title="Edit"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      className="jd-icon-btn jd-icon-btn--delete"
                      onClick={() => setDeleteTarget(d)}
                      title="Delete"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M3 4h10M6 4V3h4v1M5 4v9h6V4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Drawers & Modals ───────────────────────────────────────────────── */}
      {drawerOpen && (
        <JobDriveDrawer
          drive={editTarget}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchDrives();
          }}
        />
      )}

      {detailTarget && (
        <JobDriveDetail
          drive={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEdit={() => {
            setDetailTarget(null);
            openEdit(detailTarget);
          }}
        />
      )}

      <WarningPopup
        isOpen={!!deleteTarget && !deleteResponse}
        onClose={() => setDeleteTarget(null)}
        onDecline={() => setDeleteTarget(null)}
        onAccept={handleDeleteConfirm}
        title="Delete Job Drive"
        message={`Are you sure you want to delete "${deleteTarget?.name || `Drive #${deleteTarget?.jobDriveId}`}"? This action cannot be undone.`}
        acceptText={deleteLoading ? "Deleting…" : "Delete"}
        declineText="Cancel"
      />

      {deleteResponse && (
        <DeleteResponseModal
          response={deleteResponse}
          onForceDelete={handleForceDelete}
          forceLoading={forceLoading}
          onClose={() => {
            setDeleteResponse(null);
            setDeleteTarget(null);
          }}
        />
      )}

      {appliedTarget && (
        <AppliedCandidatesModal
          drive={appliedTarget}
          candidates={appliedCandidates}
          loading={appliedLoading}
          onClose={() => {
            setAppliedTarget(null);
            setAppliedCandidates([]);
          }}
        />
      )}

      {qrTarget && (
        <QRShareModal
          data={{ url: qrTarget.url, post: qrTarget.post }}
          onClose={() => setQrTarget(null)}
        />
      )}

      {appliedDrawerOpen && (
        <ConsultantDriveAppliedCandidates
          drives={drives}
          initialDrive={selectedAppliedDrive} // new prop
          onClose={() => {
            setAppliedDrawerOpen(false);
            setSelectedAppliedDrive(null);
          }}
        />
      )}
      {companyAppliedOpen && (
        <CompanyDriveAppliedCandidates
          drives={drives}
          onClose={() => setCompanyAppliedOpen(false)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  JobDriveDrawer — Create & Edit
// ─────────────────────────────────────────────────────────────────────────────
const JobDriveDrawer = ({ drive, onClose, onSaved }) => {
  const { isConsultant } = useWorkspace();
  const isEdit = !!drive;

  const [form, setForm] = useState(() => {
    if (!isEdit) return { ...emptyForm };
    const fromISO = drive.fromDate
      ? `${isoToDate(drive.fromDate)}T${isoToTime(drive.fromTime || drive.fromDate)}`
      : "";
    const toISO = drive.toDate
      ? `${isoToDate(drive.toDate)}T${isoToTime(drive.toTime || drive.toDate)}`
      : "";
    return {
      name: drive.name || "", // ← add this
      mode: drive.mode ? drive.mode.toUpperCase().trim() : "OFFLINE",
      fromDateTime: fromISO,
      toDateTime: toISO,
      venue: drive.venue || "",
      shareToCandidates: drive.shareToCandidates ?? true,
      postToLinkedIn: drive.postToLinkedIn ?? false,
      postToNaukri: drive.postToNaukri ?? false,
      jobPostIds: drive.jobPosts?.map((p) => p.jobPostId) || [],
      showBudget: drive.showBudget ?? true, // ← add this
      applicationClosingDate: isoToDate(drive.applicationClosingDate) || "", // ← add this
    };
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openPosts, setOpenPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);

  useEffect(() => {
    const url = isConsultant ? CONSULTANT_POSTS : COMPANY_POSTS;
    axios
      .get(url)
      .then((res) => {
        const raw = res.data.data || [];
        setOpenPosts(
          isConsultant
            ? raw.map((p) => ({
                ...p,
                jobPostId: p.consultancyJobPostId,
                jobTitle: p.roleName || `Job #${p.consultancyJobPostId}`,
                department: p.departmentName || "",
              }))
            : raw,
        );
      })
      .catch(() => {})
      .finally(() => setPostsLoading(false));
  }, [isConsultant]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const togglePostId = (id) =>
    setForm((prev) => ({
      ...prev,
      jobPostIds: prev.jobPostIds.includes(id)
        ? prev.jobPostIds.filter((x) => x !== id)
        : [...prev.jobPostIds, id],
    }));

  const validate = () => {
    const e = {};
    if (!form.mode) e.mode = "Select a mode";
    if (!form.fromDateTime) e.fromDateTime = "Required";
    if (!form.toDateTime) e.toDateTime = "Required";
    if (
      form.fromDateTime &&
      form.toDateTime &&
      form.toDateTime <= form.fromDateTime
    )
      e.toDateTime = "End must be after start";
    if (form.mode === "OFFLINE" && !form.venue.trim())
      e.venue = "Venue is required for offline drives";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildCompanyPayload = () => ({
    name: form.name || null,
    mode: form.mode,
    fromDate: form.fromDateTime,
    toDate: form.toDateTime,
    fromTime: form.fromDateTime,
    toTime: form.toDateTime,
    venue: form.venue,
    shareToCandidates: form.shareToCandidates,
    postToLinkedIn: form.postToLinkedIn,
    postToNaukri: form.postToNaukri,
    jobPostIds: form.jobPostIds,
    showBudget: form.showBudget, // ← add this
    applicationClosingDate: form.applicationClosingDate || null, // ← add this
  });

  const buildConsultantPayload = () => {
    const fromTime = form.fromDateTime?.split("T")[1] ?? null;
    const toTime = form.toDateTime?.split("T")[1] ?? null;
    return {
      name: form.name || null, // ← add this
      mode: form.mode,
      fromDate: form.fromDateTime,
      toDate: form.toDateTime,
      fromTime: fromTime ? `${fromTime}:00` : null,
      toTime: toTime ? `${toTime}:00` : null,
      venue: form.venue,
      shareToCandidates: form.shareToCandidates,
      postToLinkedIn: form.postToLinkedIn,
      postToNaukri: form.postToNaukri,
      consultancyJobPostIds: form.jobPostIds,
      showBudget: form.showBudget, // ← add this
      applicationClosingDate: form.applicationClosingDate || null, // ← add this
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    const payload = isConsultant
      ? buildConsultantPayload()
      : buildCompanyPayload();

    try {
      if (isEdit) {
        const url = isConsultant
          ? `${CONSULTANT_API}/update/${drive.jobDriveId}`
          : `${COMPANY_API}/${drive.jobDriveId}`;
        await axios.put(url, payload);
        toast.success("Job drive updated!");
      } else {
        const url = isConsultant ? CONSULTANT_API : COMPANY_API;
        await axios.post(url, payload);
        toast.success("Job drive created!");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save job drive.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">
              {isEdit ? "Edit Job Drive" : "Create Job Drive"}
            </h2>
            <p className="drawer__sub">
              {isEdit
                ? "Update drive details"
                : "Schedule a new job drive or walk-in event"}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="drawer__body">
          <div className="jd-name-mode-row">
            <div className="jd-name-mode-row__name">
              <F label="Name">
                <input
                  className="jd-input"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Campus Drive April 2026"
                />
              </F>
            </div>

            <div className="jd-name-mode-row__mode">
              <div className="jd-mode-picker">
                {["ONLINE", "OFFLINE", "HYBRID"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`jd-mode-btn${
                      form.mode === m ? " jd-mode-btn--active" : ""
                    }`}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        mode: m,
                      }))
                    }
                  >
                    {modeIcon(m)} {m}
                  </button>
                ))}
              </div>

              {errors.mode && (
                <span className="jd-field__error">{errors.mode}</span>
              )}
            </div>
          </div>
          <div className="jd-grid-2">
            <F label="Start" required error={errors.fromDateTime}>
              <input
                className={`jd-input${errors.fromDateTime ? " jd-input--error" : ""}`}
                type="datetime-local"
                name="fromDateTime"
                value={form.fromDateTime}
                onChange={handleChange}
              />
            </F>
            <F label="End" required error={errors.toDateTime}>
              <input
                className={`jd-input${errors.toDateTime ? " jd-input--error" : ""}`}
                type="datetime-local"
                name="toDateTime"
                value={form.toDateTime}
                min={form.fromDateTime || undefined}
                onChange={handleChange}
              />
            </F>
          </div>
          <F
            label="Application Closing Date"
            hint="Last date candidates can apply for posts linked to this drive"
            error={errors.applicationClosingDate}
          >
            <input
              className="jd-input"
              type="date"
              name="applicationClosingDate"
              value={form.applicationClosingDate}
              onChange={handleChange}
            />
          </F>
          <F
            label="Venue"
            required={form.mode === "OFFLINE"}
            error={errors.venue}
            hint={
              form.mode === "ONLINE"
                ? "Optional — e.g. Zoom link or platform name"
                : form.mode === "HYBRID"
                  ? "Physical address or a combination of both"
                  : "Full physical address"
            }
          >
            <input
              className={`jd-input${errors.venue ? " jd-input--error" : ""}`}
              name="venue"
              value={form.venue}
              onChange={handleChange}
              placeholder={
                form.mode === "ONLINE"
                  ? "e.g. https://zoom.us/j/... or Google Meet"
                  : "e.g. Seminar Hall B, Andheri East, Mumbai"
              }
            />
          </F>
          <div className="jd-section-label" style={{ marginTop: 10 }}>
            Sharing
          </div>{" "}
          <div className="jd-toggles">
            <Toggle
              label="Share to Candidates"
              name="shareToCandidates"
              checked={form.shareToCandidates}
              onChange={handleChange}
              icon="🌐"
            />
            <button
              type="button"
              className="jp-share-btn jp-share-btn--disabled"
              disabled
            >
              Post to LinkedIn
              <span className="jp-beta-tag">Coming Soon</span>
            </button>

            <button
              type="button"
              className="jp-share-btn jp-share-btn--disabled"
              disabled
            >
              Post to Naukri
              <span className="jp-beta-tag">Beta</span>
            </button>
          </div>
          {
            <>
              <div className="jd-section-label">
                Link Job Posts
                {form.jobPostIds.length > 0 && (
                  <span className="jd-count-pill">
                    {form.jobPostIds.length} selected
                  </span>
                )}
              </div>
              {postsLoading ? (
                <div className="jd-posts-loading">
                  Loading open job posts...
                </div>
              ) : openPosts.length === 0 ? (
                <div className="jd-posts-empty">
                  No open job posts available to link. Create a job post first.
                </div>
              ) : (
                <div className="jd-post-list">
                  {openPosts.map((p) => {
                    const selected = form.jobPostIds.includes(p.jobPostId);
                    return (
                      <div
                        key={p.jobPostId}
                        className={`jd-post-item${selected ? " jd-post-item--selected" : ""}`}
                        onClick={() => togglePostId(p.jobPostId)}
                      >
                        <div className="jd-post-item__check">
                          {selected ? (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <circle cx="9" cy="9" r="9" fill="#2563eb" />
                              <path
                                d="M5 9l3 3 5-5"
                                stroke="#fff"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <div className="jd-post-item__empty-check" />
                          )}
                        </div>
                        <div className="jd-post-item__info">
                          <div className="jd-post-item__title">
                            {p.jobTitle}
                          </div>
                          <div className="jd-post-item__meta">
                            {p.department && <span>🏢 {p.department}</span>}
                            {p.noOfVacancy && (
                              <span>👥 {p.noOfVacancy} pos.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          }
        </div>

        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <span className="cv-btn__spinner" />
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Drive"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  JobDriveDetail
// ─────────────────────────────────────────────────────────────────────────────
const JobDriveDetail = ({ drive: d, onClose, onEdit }) => {
  const status = getDriveStatus(d);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">
              {d.name || `Drive #${d.jobDriveId}`}
            </h2>
            <p className="drawer__sub">
              #{d.jobDriveId} · {d.mode} · {fmtDate(d.fromDate)}
              {isoToDate(d.fromDate) !== isoToDate(d.toDate)
                ? ` → ${fmtDate(d.toDate)}`
                : ""}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="drawer__body">
          <div className="jd-detail-badges">
            <span
              className={`jd-mode-badge jd-mode-badge--${d.mode?.toLowerCase()}`}
            >
              {modeIcon(d.mode)} {d.mode}
            </span>
            <span className={status.cls}>{status.label}</span>
          </div>

          <div className="jd-detail-schedule">
            <div className="jd-detail-schedule__block">
              <div className="jd-detail-schedule__label">Start</div>
              <div className="jd-detail-schedule__date">
                {fmtDate(d.fromDate)}
              </div>
              <div className="jd-detail-schedule__time">
                {fmtTime(d.fromTime)}
              </div>
            </div>
            <div className="jd-detail-schedule__sep">→</div>
            <div className="jd-detail-schedule__block">
              <div className="jd-detail-schedule__label">End</div>
              <div className="jd-detail-schedule__date">
                {fmtDate(d.toDate)}
              </div>
              <div className="jd-detail-schedule__time">
                {fmtTime(d.toTime)}
              </div>
            </div>
          </div>

          {d.venue && (
            <div className="jd-detail-venue">
              <span>📍</span>
              <span>{d.venue}</span>
            </div>
          )}

          {(() => {
            const budgetPost = d.jobPosts?.find((p) => p.budget > 0);
            const budgetDisplay =
              d.showBudget && budgetPost
                ? `₹${Number(budgetPost.budget).toLocaleString("en-IN")}${
                    budgetPost.budgetType
                      ? ` / ${budgetPost.budgetType.toLowerCase()}`
                      : ""
                  }`
                : null;
            const createdByName =
              d.createdByName || d.createdBy?.name || d.createdByEmployeeName;

            if (!d.applicationClosingDate && !budgetDisplay && !createdByName)
              return null;

            return (
              <div className="jd-detail-venue" style={{ marginTop: 8 }}>
                {d.applicationClosingDate && (
                  <span style={{ marginRight: 16 }}>
                    🗓️ Apply by {fmtDate(d.applicationClosingDate)}
                  </span>
                )}
                {budgetDisplay && (
                  <span style={{ marginRight: 16 }}>💰 {budgetDisplay}</span>
                )}
                {createdByName && <span>👤 Created by {createdByName}</span>}
              </div>
            );
          })()}

          {d.jobPosts?.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div className="jd-section-label">
                Linked Job Posts ({d.jobPosts.length})
              </div>
              <div className="jd-detail-posts">
                {d.jobPosts.map((p) => (
                  <div key={p.jobPostId} className="jd-detail-post">
                    <div className="jd-detail-post__title">{p.jobTitle}</div>
                    <div className="jd-detail-post__meta">
                      {p.department && <span>🏢 {p.department}</span>}
                      {p.noOfVacancy && (
                        <span>
                          👥 {p.noOfVacancy} position
                          {p.noOfVacancy !== 1 ? "s" : ""}
                        </span>
                      )}
                      <span
                        className={`jd-status-dot${p.status === "OPEN" ? " jd-status-dot--open" : ""}`}
                        style={{ display: "inline-block" }}
                      />
                      <span>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
          <div style={{ flex: 1 }} />
          <button className="cv-btn cv-btn--primary" onClick={onEdit}>
            Edit Drive
          </button>
        </div>
      </div>
    </>
  );
};

export default AllJobDrives;
